import React, { useCallback, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { convertToDate, serializeDate } from 'utils';
import errorCb from 'Common/Utils/errorCb';
import EditAmendmentView from './EditAmendment.view';
import { fetchReasons, saveAmendment } from '../AmendContract.service';
import useGrouping from 'Common/Utils/Hooks/useGrouping';

function EditAmendment({
    amendment,
    mode,
    onBack
}) {
    const globalDispatch = useDispatch();
    const grouping = useGrouping();
    const [amendmentDate, setAmendmentDate] = useState(() => convertToDate(amendment.amendmentDate));
    const [changed, setChanged] = useState(false);
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
    const reasonsDataSource = useMemo(() =>
        fetchReasons(grouping, amendment.type, errorCb([], "Error fetching amendment reasons.", globalDispatch)),
    [globalDispatch, grouping, amendment]);
    const [reasonsSelected, setReasonsSelected] = useState([]);
    const [saving, setSaving] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [errorMessage, setErrorMessage] = useState();
    const cancelled = useRef(false);

    const handleCancelClick = useCallback(() => {
        if (changed) {
            setShowCancelConfirmation(true);
        } else {
            cancelled.current = true;
            onBack();
        }
    }, [onBack, changed]);

    const handleCancel = useCallback(cancel => {
        if (cancel) {
            cancelled.current = true;
            onBack();
        } else {
            setShowCancelConfirmation(false);
        }
    }, [onBack]);

    const handleDateChange = useCallback(value => {
        if (value instanceof Date) {
            value.setHours(0, 0, 0, 0);
        }

        validate({ amendmentDate: value }, setValidationErrors);
        setAmendmentDate(value);
        setChanged(true);
    }, []);

    const handleReasonsChange = useCallback(value => {
        validate({ reasonsSelected: value }, setValidationErrors);
        setReasonsSelected(value);
        setChanged(true);
    }, []);

    const handleReasonsLoad = useCallback(reasons => {
        if (mode === 'edit' && amendment.amendmentReasonIds && amendment.amendmentReasonIds.length) {
            const selected = reasons.filter(r => amendment.amendmentReasonIds.indexOf(r.questionId) !== -1);
            setReasonsSelected(selected);
        }
    }, [amendment, mode]);

    const handleSaveClick = useCallback(async () => {
        if (!validate({ reasonsSelected, amendmentDate }, setValidationErrors)) {
            return;
        }

        setSaving(true);
        const errorCallback = message => {
            setErrorMessage('Error saving amendment. ' + message);
            setSaving(false);
        };
        const newData = {
            ...amendment,
            amendmentDate: serializeDate(amendmentDate),
            amendmentReasonIds: reasonsSelected.map(reason => reason.questionId),
        };

        if (await saveAmendment(newData, errorCallback) && !cancelled.current) {
            setSaving(false);
            onBack();
        }
    }, [reasonsSelected, amendmentDate, onBack, amendment]);

    return (
        <EditAmendmentView
            title={mode === 'add' ? 'Add Amendment' : 'Edit Amendment'}
            allowSave={!saving}
            amendmentNumber={amendment.amendmentNumber}
            amendmentDate={amendmentDate}
            errorMessage={errorMessage}
            reasonsDataSource={reasonsDataSource}
            reasonsSelected={reasonsSelected}
            showCancelConfirmation={showCancelConfirmation}
            validationErrors={validationErrors}
            onCancel={handleCancel}
            onCancelClick={handleCancelClick}
            onDateChange={handleDateChange}
            onReasonsChange={handleReasonsChange}
            onReasonsLoad={handleReasonsLoad}
            onSaveClick={handleSaveClick}
        />
    )
}

function validate({ reasonsSelected, amendmentDate }, setValidationErrors) {
    const errors = {};
    let valid = true;
    if (reasonsSelected !== undefined) {
        if (!reasonsSelected || reasonsSelected.length === 0) {
            errors.reasons = 'At least one must be selected.';
            valid = false;
        } else if (reasonsSelected.length > 6) {
            errors.reasons = 'No more than 6 reasons may be selected.';
            valid = false;
        } else {
            errors.reasons = null;
        }
    }

    if (amendmentDate !== undefined) {
        const today = new Date().setHours(0, 0, 0, 0);
        if (!amendmentDate || amendmentDate < today) {
            errors.amendmentDate = 'Must be greater than or equal to today.';
            valid = false;
        } else {
            errors.amendmentDate = null;
        }
    }

    setValidationErrors(prev => ({ ...prev, ...errors }));
    return valid;
}

EditAmendment.propTypes = {
    amendment: PropTypes.object.isRequired,
    mode: PropTypes.oneOf(['add', 'edit']).isRequired,
    onBack: PropTypes.func.isRequired,
};

export default EditAmendment;